import React from 'react';
import { getRichTextMarkup, StoryblokCommon } from '../../../lib/storyblok/storyblok';
import HsButton from '../hs-button/hs-button';
import { CookieNotificationStoryblok } from '../../../_shared/interfaces/storyblok';
import { getLinkFragment } from '../../../lib/link-fragment/link-fragment';

import * as CookieNotificationStyles from './cookie-notification.module.scss';

import * as FormStylesHs from '../../../_scss/modules/_forms.hs.module.scss';
import * as FormStylesDr from '../../../_scss/modules/_forms.dr.module.scss';

export const COOKIES_ACCEPTED_STORAGE_NAME = 'cookies-accepted';
declare global {
    interface Window {
        cookieConsent: any;
    }
}

const CookieNotification = ({
    showTrigger,
    blok,
    storyblokCommon,
}: {
    showTrigger: number;
    blok: CookieNotificationStoryblok;
    storyblokCommon: StoryblokCommon;
}) => {
    const TRACKING_SCRIPT_URL = `${process.env.GATSBY_TRACKING_SCRIPT}`;
    const TRACKING_SCRIPT_ID = 'tracking-script';
    const AcceptedCookieStorageName = typeof window !== 'undefined' ? localStorage.getItem(COOKIES_ACCEPTED_STORAGE_NAME) : null;
    const [isHidden, setIsHidden] = React.useState(true);
    const [trackingAllowed, setTrackingAllowed] = React.useState(typeof window !== 'undefined' && AcceptedCookieStorageName === 'all');
    const FormStyles = storyblokCommon.configuration.content.theme === 'hs' ? FormStylesHs : FormStylesDr;

    const loadTrackingScript = () => {
        if (typeof window !== 'undefined') {
            if (AcceptedCookieStorageName) {
                setGlobalCookieConsent(AcceptedCookieStorageName === 'all' ? true : false);
            }
            const existingScript = document.getElementById(TRACKING_SCRIPT_ID);
            if (!existingScript) {
                const script = document.createElement('script');
                script.src = TRACKING_SCRIPT_URL;
                script.id = TRACKING_SCRIPT_ID;
                script.async = true;
                document.head.appendChild(script);
            }
        }
    };

    const setGlobalCookieConsent = (all: boolean = false) => {
        if (typeof window !== 'undefined') {
            window.cookieConsent = {
                required: true,
                performance: true,
                all,
            };
        }
    };

    const closeNotification = (button: 'all' | 'settings') => {
        setIsHidden(true);

        if (button === 'all' || trackingAllowed) {
            setTrackingAllowed(true);
            localStorage.setItem(COOKIES_ACCEPTED_STORAGE_NAME, 'all');
            setGlobalCookieConsent(true);
        } else {
            localStorage.setItem(COOKIES_ACCEPTED_STORAGE_NAME, 'required');

            setGlobalCookieConsent(false);
        }
        triggerTrackingEvent();
    };

    const triggerTrackingEvent = () => {
        if (typeof window !== 'undefined') {
            const event = new Event('cookie-consent-update');
            window.dispatchEvent(event);
        }
    };

    React.useEffect(() => {
        const userAgreement = AcceptedCookieStorageName;
        if (!userAgreement) {
            setIsHidden(false);
        }
        loadTrackingScript();
    }, []);

    React.useEffect(() => {
        if (showTrigger) {
            setIsHidden(false);
        }
    }, [showTrigger]);

    return (
        <>
            {!isHidden && (
                <div className={CookieNotificationStyles.cookieNotification}>
                    <div>
                        <h4 className={CookieNotificationStyles.headline}>{blok.headline}</h4>

                        <div dangerouslySetInnerHTML={getRichTextMarkup(blok.copytext)} />

                        <form>
                            <div>
                                <input id="cookies-required" type="checkbox" checked={true} disabled={true} />
                                <label htmlFor="cookies-required" className={FormStyles.checkboxLabel}>
                                    {blok.checkbox_required}
                                </label>
                            </div>

                            <div>
                                <input
                                    id="cookies-tracking"
                                    type="checkbox"
                                    checked={trackingAllowed}
                                    onChange={() => setTrackingAllowed(!trackingAllowed)}
                                />
                                <label htmlFor="cookies-tracking" className={FormStyles.checkboxLabel}>
                                    {blok.checkbox_tracking}
                                </label>
                            </div>
                        </form>

                        <div className={CookieNotificationStyles.wrapper}>
                            <ul>
                                {blok.links &&
                                    blok.links.map((navEntry) => {
                                        return <li key={navEntry._uid}>{getLinkFragment(navEntry)}</li>;
                                    })}
                            </ul>

                            <div className={CookieNotificationStyles.buttons}>
                                <HsButton
                                    appearance="secondary"
                                    onClick={() => closeNotification('settings')}
                                    storyblokCommon={storyblokCommon}
                                >
                                    {blok.button_confirm}
                                </HsButton>
                                <HsButton appearance="primary" onClick={() => closeNotification('all')} storyblokCommon={storyblokCommon}>
                                    {blok.button_accept}
                                </HsButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieNotification;
